import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Container, Row, Button, Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, Table } from "reactstrap";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { getVendorSettings, setVendorData } from "../../services/helper";
import CommonCardHeader from "../../Utils/CommonComponents/CommonCardHeader";

export default function EventDiscountsPage() {
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    dates: "",
    type: "percentage",
    value: "",
    items: "",
    minAmount: "",
    buyItem: "",
    getItem: "",
    secondItemDiscount: "",
    recurring: false
  });

  const { products } = useSelector((state) => state.products);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState("");

  const discountTypes = [
    { value: "percentage", label: "Percentage Off" },
    { value: "fixed_amount", label: "Fixed Amount Off" },
    { value: "buy_one_get_one", label: "Buy One Get One" },
    { value: "free_item", label: "Free Item" },
    { value: "no_tax", label: "No Tax" },
    { value: "second_item_discount", label: "Second Item Discount" },
    { value: "minimum_purchase", label: "Minimum Purchase Discount" }
  ];



  useEffect(() => {
    loadEvents();
  }, []);



  const loadEvents = () => {
    const savedEvents = getVendorSettings("event_discounts");
    if (savedEvents) {
      try {
        setEvents(JSON.parse(savedEvents));
      } catch (error) {
        console.error("Failed to parse event discounts:", error);
        setEvents([]);
      }
    }
  };

  const saveEvents = async (updatedEvents) => {
    try {
      await setVendorData({ event_discounts: JSON.stringify(updatedEvents) });
      setEvents(updatedEvents);
      toast.success("Event discounts saved successfully!");
    } catch (error) {
      console.error("Error saving events:", error);
      toast.error("Failed to save events");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newEvent = {
      id: editingEvent?.id || Date.now().toString(),
      name: formData.name,
      dates: formData.dates.split(",").map(d => d.trim()),
      type: formData.type,
      value: formData.value,
      items: formData.items.split(",").map(i => i.trim()).filter(i => i),
      minAmount: formData.minAmount,
      buyItem: formData.buyItem,
      getItem: formData.getItem,
      secondItemDiscount: formData.secondItemDiscount,
      recurring: formData.recurring
    };

    let updatedEvents;
    if (editingEvent) {
      updatedEvents = events.map(e => e.id === editingEvent.id ? newEvent : e);
    } else {
      updatedEvents = [...events, newEvent];
    }

    saveEvents(updatedEvents);
    resetForm();
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      name: event.name,
      dates: event.dates.join(", "),
      type: event.type,
      value: event.value,
      items: event.items.join(", "),
      minAmount: event.minAmount || "",
      buyItem: event.buyItem || "",
      getItem: event.getItem || "",
      secondItemDiscount: event.secondItemDiscount || "",
      recurring: event.recurring
    });
    setShowModal(true);
  };

  const handleDelete = (eventId) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      const updatedEvents = events.filter(e => e.id !== eventId);
      saveEvents(updatedEvents);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      dates: "",
      type: "percentage",
      value: "",
      items: "",
      minAmount: "",
      buyItem: "",
      getItem: "",
      secondItemDiscount: "",
      recurring: false
    });
    setEditingEvent(null);
    setShowModal(false);
    setShowSuggestions(false);
  };

  const renderDynamicFields = () => {
    const ProductSelect = ({ field, label, required = false }) => (
      <FormGroup>
        <Label>{label}</Label>
        <Input
          type="select"
          value={formData[field]}
          onChange={(e) => setFormData({...formData, [field]: e.target.value})}
          required={required}
        >
          <option value="">Select a product...</option>
          {products.map(product => (
            <option key={product.id} value={product.name}>{product.name}</option>
          ))}
        </Input>
      </FormGroup>
    );

    const ProductMultiSelect = ({ field, label }) => (
      <FormGroup>
        <Label>{label}</Label>
        <Input
          type="select"
          multiple
          value={formData[field] ? formData[field].split(',').map(i => i.trim()) : []}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions, option => option.value);
            setFormData({...formData, [field]: selected.join(', ')});
          }}
          style={{minHeight: '120px'}}
        >
          {products.map(product => (
            <option key={product.id} value={product.name}>{product.name}</option>
          ))}
        </Input>
        <small className="text-muted">Hold Ctrl/Cmd to select multiple items, or leave empty for all items</small>
      </FormGroup>
    );

    switch (formData.type) {
      case "minimum_purchase":
        return (
          <>
            <FormGroup>
              <Label>Minimum Purchase Amount ($)</Label>
              <Input
                type="number"
                value={formData.minAmount}
                onChange={(e) => setFormData({...formData, minAmount: e.target.value})}
                placeholder="25"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Discount Amount ($)</Label>
              <Input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: e.target.value})}
                placeholder="5"
                required
              />
            </FormGroup>
          </>
        );
      
      case "buy_one_get_one":
        return (
          <>
            <ProductSelect field="buyItem" label="Buy Item" required />
            <ProductSelect field="getItem" label="Get Item (Free)" required />
          </>
        );
      
      case "second_item_discount":
        return (
          <>
            <ProductSelect field="buyItem" label="Item" required />
            <FormGroup>
              <Label>Second Item Discount (%)</Label>
              <Input
                type="number"
                value={formData.secondItemDiscount}
                onChange={(e) => setFormData({...formData, secondItemDiscount: e.target.value})}
                placeholder="50"
                required
              />
            </FormGroup>
          </>
        );
      
      case "free_item":
        return <ProductSelect field="items" label="Free Item" required />;
      
      case "percentage":
      case "fixed_amount":
        return (
          <>
            <FormGroup>
              <Label>{formData.type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}</Label>
              <Input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: e.target.value})}
                placeholder={formData.type === 'percentage' ? '10' : '5'}
                required
              />
            </FormGroup>
            <ProductMultiSelect field="items" label="Items" />
          </>
        );
      
      case "no_tax":
        return (
          <ProductMultiSelect field="items" label="Items" />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="page-body main-project">
      <Container fluid>
        <Row>
          <Col sm={12}>
            <Card>
              <CommonCardHeader title="Event Discounts" />
              <CardBody>
                <div className="d-flex justify-content-between mb-3">
                  <h5>Manage Event Discounts</h5>
                  <Button color="primary" onClick={() => setShowModal(true)}>
                    Add New Event
                  </Button>
                </div>

                <Table responsive>
                  <thead>
                    <tr>
                      <th>Event Name</th>
                      <th>Dates</th>
                      <th>Type</th>
                      <th>Value</th>
                      <th>Items</th>
                      <th>Recurring</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map(event => (
                      <tr key={event.id}>
                        <td>{event.name}</td>
                        <td>{event.dates.join(", ")}</td>
                        <td>{discountTypes.find(t => t.value === event.type)?.label}</td>
                        <td>
                          {event.type === 'minimum_purchase' ? `$${event.value} off $${event.minAmount}+` :
                           event.type === 'second_item_discount' ? `${event.secondItemDiscount}% off 2nd` :
                           event.type === 'buy_one_get_one' ? 'BOGO' :
                           event.type === 'free_item' ? 'Free' :
                           event.type === 'no_tax' ? 'No Tax' :
                           event.type === 'percentage' ? `${event.value}%` : `$${event.value}`}
                        </td>
                        <td>
                          {event.type === 'buy_one_get_one' ? `${event.buyItem} → ${event.getItem}` :
                           event.type === 'second_item_discount' ? event.buyItem :
                           event.type === 'free_item' ? event.items :
                           event.items ? (Array.isArray(event.items) ? event.items.join(", ") : event.items) : 'All items'}
                        </td>
                        <td>{event.recurring ? "Yes" : "No"}</td>
                        <td>
                          <Button size="sm" color="info" onClick={() => handleEdit(event)} className="me-2">
                            Edit
                          </Button>
                          <Button size="sm" color="danger" onClick={() => handleDelete(event.id)}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={showModal} toggle={resetForm}>
        <ModalHeader toggle={resetForm}>
          {editingEvent ? "Edit Event" : "Add New Event"}
        </ModalHeader>
        <ModalBody onClick={(e) => e.stopPropagation()}>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Event Name</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Dates (comma separated, format: YYYY-MM-DD)</Label>
              <Input
                type="text"
                value={formData.dates}
                onChange={(e) => setFormData({...formData, dates: e.target.value})}
                placeholder="2024-09-01, 2024-09-08"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Discount Type</Label>
              <Input
                type="select"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
              >
                {discountTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </Input>
            </FormGroup>

            {renderDynamicFields()}

            <FormGroup check>
              <Input
                type="checkbox"
                checked={formData.recurring}
                onChange={(e) => setFormData({...formData, recurring: e.target.checked})}
              />
              <Label check>Recurring Yearly</Label>
            </FormGroup>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button type="button" color="secondary" onClick={resetForm}>
                Cancel
              </Button>
              <Button type="submit" color="primary">
                {editingEvent ? "Update" : "Create"} Event
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  );
}